import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import OutsideClickComponent from '../../fragments/OutsideClickComponent'
import { isLightColor } from '../../../lib/script'
import { useCanChangeArchivedUnarchived, useUserTabEditPermissionDisabled, useCanEditArchived } from '../../../lib/usersPermissions'

import classes from './CustomSelect.module.css'
import RemoveDataConfirmationModal from '../RemoveDataConfirmationModal'
import BodySimpleModal from '../modal/BodySimpleModal'
import { generateRandomString } from '../../../lib/misc'
import { isInsidePortalModal } from '../../../lib/overlayLayers'

export default function CustomSelect({name, label, optionsData, preSelectedValue, action, identifier, sameSizeAsContainer, minWidth, reverseDrawer, rtlDrawer, bkTransparent, borderRed, hasRemove, noticeDisabled, isDisabled, thin_select, fixedDrawer = true, undetermined, isAFilter, ignoreDeactivatedOptions, required, isMismatched, isSystemMismatch, changeArchivedUnarchive, complexLabel, appendToText, apiDisabled, showApiLoader, focusOnMount, fallbackSelectedOption}) {
    // let masterDisable = useCanEdit()
    let changeArchivedStatusDisabled = useCanChangeArchivedUnarchived() && changeArchivedUnarchive
    let editArchivedDisabled = useCanEditArchived(changeArchivedUnarchive)
    let userPermissionDisabled = useUserTabEditPermissionDisabled(identifier?.permissionType ? identifier?.permissionType : identifier?.type)

   
    const containerRef = useRef(null)
    const inputRef = useRef(null)
    const drawerRef = useRef(null)
    const focusOnMountDoneRef = useRef(false)
    const [coordinates, setCoordinates] = useState({
        top: null,
        left: null,
        width: null
    })


    const [showDrawer, setShowDrawer] = useState(false)
    const [selectedHidden, setSelectedHidden] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [selectedValue, setSelectedValue] = useState(null)
    const [bkColor, setBkColor] = useState(null)
    const [textColor, setTextColor] = useState(null)

    const [openConfirmationModal, setOpenConfirmationModal] = useState(false)

    const [options, setOptions] = useState(optionsData)

    const fallbackResolvedSelection = useMemo(() => {
        if (!fallbackSelectedOption) return null
        if (preSelectedValue === '' || preSelectedValue == null) return null
        try {
            const opt = fallbackSelectedOption(preSelectedValue)
            return opt ?? null
        } catch {
            return null
        }
    }, [fallbackSelectedOption, preSelectedValue])
    
    function inputBlurHandle() {
        setInputValue('')
    }
    const blurHandler = useCallback(() => {
        setShowDrawer(false)
        setSelectedHidden(false)
        setOptions(optionsData) // clear the filters
    }, [optionsData])

    function changeInputhandler(event) {
        if (!userPermissionDisabled) { // check for user permission first
            setInputValue(event.target.value ? event.target.value : '')
            const query = (event.target.value ?? '').toString().toLowerCase()
            let filteredOptions = optionsData.filter(item => item.label.toString().toLowerCase().includes(query))
            setOptions(filteredOptions) 
        }
               
    }

    const updateFixedDrawerCoordinates = useCallback(() => {
        if (!containerRef.current) {
            return
        }
        const rect = containerRef.current.getBoundingClientRect()
        const left = rect.left
        const width = rect.width
        const top = rect.top
        const bottom = rect.bottom
        const windowHeight = window.innerHeight

        if (windowHeight - bottom < 300) {
            setCoordinates({
                bottom: window.innerHeight - top + 8,
                left,
                width,
                top: 'unset',
            })
        } else {
            setCoordinates({
                top: bottom + 4,
                left,
                width,
                bottom: undefined,
            })
        }
    }, [])

    function focusHandler() {
        if (!userPermissionDisabled) { // check for user permission first
            setShowDrawer(true) // show drawer on focus
            setSelectedHidden(true) //if there is a selected value, need to set it hidden

            if (fixedDrawer) {
                updateFixedDrawerCoordinates()
            }
        }
    }

    useLayoutEffect(() => {
        if (showDrawer && fixedDrawer) {
            updateFixedDrawerCoordinates()
        }
    }, [showDrawer, fixedDrawer, updateFixedDrawerCoordinates])  

    function setOptionHandler(item) {
        if (!userPermissionDisabled) { // check for user permission first
            if (item?.value != preSelectedValue) {
                setInputValue('') //clear input
                setShowDrawer(false) //close drawer
                setSelectedHidden(false) //show the selected value
                setOptions(optionsData) // clear the filters

                // Always reflect the new selection immediately in UI.
                // Some quick-update flows intentionally defer Redux merges, so relying only
                // on `preSelectedValue` can make selects appear stale until API/state sync.
                setSelectedValue(item)
            
                if (action) {
                    action({
                        data: {
                            value: item.value
                        },
                        identifier: identifier,
                        option: item
                    })
                }
            }
        }
        
    }

    const setOptionHandlerEffect = useCallback((item) => {
        setInputValue('') //clear input
        setSelectedValue(item) //set value
        setShowDrawer(false) //close drawer
        setSelectedHidden(false) //show the selected value
        setOptions(optionsData) // clear the filters      
        
    }, [optionsData])


    function clearSelectionHandler() {
        if (!userPermissionDisabled) { // check for user permission first

            setShowDrawer(false)
            if (selectedValue) {
                if (!isAFilter) {
                    setOpenConfirmationModal(true)
                } else {
                    removeSelection()
                }
            }
        }
        
    }
    function closeConfirmationModal() {
        setOpenConfirmationModal(false)
    }

    function removeSelection() {
        
        setInputValue('') //clear input
        setShowDrawer(false) //close drawer
        setSelectedHidden(false) //show the selected value
        setOptions(optionsData) // clear the filters
    
        setSelectedValue(null)
    
        if (action) {
            action({
                data: {
                    value: null
                },
                identifier: identifier,
                isRemove: true
            })
        }
        setOpenConfirmationModal(false)
    }

    const justToggledClosedByMouseDownRef = useRef(false)

    function focusSelectOnClickHandler() {
        if (userPermissionDisabled) return
        // mousedown handler may have just closed the drawer for this same click;
        // skip the click so we don't immediately reopen it.
        if (justToggledClosedByMouseDownRef.current) {
            justToggledClosedByMouseDownRef.current = false
            return
        }
        if (showDrawer) return
        setShowDrawer(true)
        setSelectedHidden(true)
        if (fixedDrawer) {
            updateFixedDrawerCoordinates()
        }
        if (inputRef && inputRef.current) {
            inputRef.current.focus()
        }
    }

    function toggleOnMouseDownHandler(event) {
        if (userPermissionDisabled) return
        if (showDrawer) {
            // Closing: prevent the input from being (re)focused, which would
            // otherwise re-open the drawer via onFocus before the click fires.
            event.preventDefault()
            justToggledClosedByMouseDownRef.current = true
            blurHandler()
            inputRef.current?.blur()
        }
    }

    // backgroundColor important
    useLayoutEffect(() => {
        if (containerRef && containerRef.current) {
            containerRef.current.style.setProperty("background-color", bkColor, "important");
        }
    }, [bkColor]);



    useEffect(() => {
        if (optionsData ) { //&& preSelectedValue
            setOptions(optionsData)
            
            let foundOption = optionsData.find(item => item.value == preSelectedValue)
       
            if (foundOption) {
                setOptionHandlerEffect(foundOption)

                if (foundOption.color_code) {
                    setBkColor(foundOption.color_code)
                    let lightDark = isLightColor(foundOption.color_code)
                    setTextColor(lightDark ? 'var(--text)' : 'var(--white)')
                    
                } else {
                    setTextColor(null)
                    setBkColor(null)
                    // setNoBorder(false)
                }
            } else {
                if (fallbackResolvedSelection) {
                    setOptionHandlerEffect(fallbackResolvedSelection)
                    if (fallbackResolvedSelection.color_code) {
                        setBkColor(fallbackResolvedSelection.color_code)
                        let lightDark = isLightColor(fallbackResolvedSelection.color_code)
                        setTextColor(lightDark ? 'var(--text)' : 'var(--white)')
                    } else {
                        setTextColor(null)
                        setBkColor(null)
                    }
                } else {
                    setSelectedValue(null)
                    setTextColor(null)
                    setBkColor(null)
                }
            }
        } else {
            setInputValue('') //clear input
            setShowDrawer(false) //close drawer
            setSelectedHidden(false) //show the selected value        
            setSelectedValue(null)
            setTextColor(null)
            setBkColor(null)
        }
    }, [optionsData, preSelectedValue, setOptionHandlerEffect, fallbackResolvedSelection])

    useEffect(() => {
        if (!showDrawer) {
            return
        }

        const onScrollOrResize = () => {
            if (fixedDrawer) {
                updateFixedDrawerCoordinates()
                return
            }
            blurHandler()
            inputBlurHandle()
            inputRef.current?.blur()
        }

        // Capture scroll on nested containers (work-queue table, tab overflow areas).
        document.addEventListener('scroll', onScrollOrResize, true)
        window.addEventListener('resize', onScrollOrResize, { passive: true })
        return () => {
            document.removeEventListener('scroll', onScrollOrResize, true)
            window.removeEventListener('resize', onScrollOrResize)
        }
    }, [showDrawer, fixedDrawer, blurHandler, updateFixedDrawerCoordinates])


    const isControlDisabled = Boolean(apiDisabled || isDisabled || changeArchivedStatusDisabled || editArchivedDisabled || userPermissionDisabled)
    const outsideClickExtraInsideRefs = useMemo(() => fixedDrawer ? [drawerRef] : undefined, [fixedDrawer])

    useLayoutEffect(() => {
        if (!focusOnMount || isControlDisabled || focusOnMountDoneRef.current) return;
        focusOnMountDoneRef.current = true;
        const t = window.setTimeout(() => {
            inputRef.current?.focus({ preventScroll: true });
        }, 0);
        return () => clearTimeout(t);
    }, [focusOnMount, isControlDisabled]);

    if (!optionsData) { // no options were sent        
        return <div className={`${classes.container} `} style={{minWidth: minWidth ? minWidth : ''}}>
            {/* onBlur={blurHandler} */}
            <OutsideClickComponent task={blurHandler}>
                
                <div className={classes.input_container}>
                    {label && <span className={`${classes.label} label`}>{label}{required ? <span className={'required'}>*</span> : ''}{showApiLoader && apiDisabled ? <span className="loader dark field_label_loader" /> : null}</span>}
                    <input name={name + '_no_options'} type='text' className={classes.input} autoComplete="off" onChange={() => {}} value={inputValue ? inputValue : ''}/>
                    <div className={classes.selected}>No data available</div>
                    <div className={`select_pointer ${classes.pointer}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="#577988" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                </div>
            </OutsideClickComponent>
        </div>
    }  

    const insideModal = fixedDrawer && isInsidePortalModal(containerRef.current)

    const drawerNode = showDrawer ? (
        <div
            ref={drawerRef}
            className={`${classes.drawer} ${(reverseDrawer && !fixedDrawer) ? classes.reverse_drawer : ''} ${rtlDrawer ? classes.rtl_drawer : ''} ${fixedDrawer ? classes.fixed : ''} ${insideModal ? classes.fixed_above_modal : ''}`}
            style={{ top: coordinates.top, left: coordinates.left, width: coordinates.width, bottom: coordinates.bottom }}
        >
            {options && options.filter(item => !item.isDisabled).map(item => (
                <div key={item.value}>
                    <button type='button' onClick={setOptionHandler.bind(this, item)} className={`${classes.option} ${(selectedValue && (selectedValue.value == item.value)) ? classes.option_selected : ''}`} disabled={isControlDisabled || (item.manual_selection && !ignoreDeactivatedOptions)}>
                        {complexLabel ? <div dangerouslySetInnerHTML={{ __html: item.complexLabel }}></div> : item.label}
                    </button>
                </div>
            ))}
        </div>
    ) : null

    const overlayRoot = typeof document !== 'undefined' ? document.getElementById('overlay-root') : null

    const showMismatchStyle = isMismatched || isSystemMismatch

    return <div className={`${classes.container} ${(showDrawer || selectedValue || undetermined) ? classes.is_focused : ''} ${showMismatchStyle ? classes.is_mismatched : ''} ${!label && classes.no_label_container} ${thin_select ? classes.thin_select : ''} ${sameSizeAsContainer ? classes.same_size_as_container : ''} ${isControlDisabled ? classes.is_inactive : ''} `} style={{minWidth: minWidth ? minWidth : ''}}>
        <OutsideClickComponent task={blurHandler} extraInsideRefs={outsideClickExtraInsideRefs}>
            {/* backgroundColor: bkColor,  */}
            <div className={`${classes.input_container_wrapper}`} style={{color: textColor}}>
                <div ref={containerRef} onMouseDown={toggleOnMouseDownHandler} onClick={focusSelectOnClickHandler} className={`${classes.input_container} ${hasRemove ? classes.has_remove_button : ''} ${(selectedValue && selectedValue.color) && 'bk_' + selectedValue.color.split(' ').join('_')} ${bkTransparent ? classes.background_transparent: ''} ${borderRed ? classes.border_red : ''}`} style={{color: textColor}}>
                    {label && <div className={`${classes.label} label`} style={{color: textColor}}>{label}{required ? <span className={'required'}>*</span> : ''}{showApiLoader && apiDisabled ? <span className="loader dark field_label_loader" /> : null}</div>}
                    
                
                    <input name={generateRandomString(20)} id={generateRandomString(20)} ref={inputRef} type='text' onChange={changeInputhandler} onFocus={focusHandler} onBlur={inputBlurHandle} className={classes.input} value={inputValue ? inputValue: ''} autoComplete="__off" title={selectedValue?.label} disabled={isControlDisabled} />
                    
                    {/* type='hidden' */}
                    <input name={name}  defaultValue={selectedValue?.value ? selectedValue?.value : ''} autoComplete="off" required={required} type='hidden' style={{opacity: 0, float: 'left', width:0, height: 1, position: 'absolute'}}/>

                    {selectedValue && <div className={classes.selected_shadow} style={{opacity: 0}}>{selectedValue.label}</div>}
                    {selectedValue && <div className={`${classes.selected} selected`} style={{opacity: selectedHidden ? 0 : 1, color: textColor}} >{selectedValue.label} {appendToText ? appendToText : ''}</div>}

                    {undetermined && <div className={classes.selected_shadow} style={{opacity: 0}}>{undetermined}</div>}
                    {undetermined && <div className={`${classes.selected} selected`} style={{opacity: selectedHidden ? 0 : 1, color: textColor}}>{undetermined}</div>}
                    <div className={`select_pointer ${classes.pointer} ${showDrawer && classes.opened}`}>
                        {showApiLoader && apiDisabled ? (
                            <span className="loader dark field_label_loader" style={{marginLeft: 0}} />
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="#577988" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        )}
                    </div>
                    
                </div>
                {hasRemove && <button type='button' className={`${classes.clear_select} ${(selectedValue && selectedValue.color) && 'bk_' + selectedValue.color.split(' ').join('_')} custom_select_clear_button`} onClick={clearSelectionHandler} disabled={isControlDisabled}><i style={{color: textColor}} className='ti ti-x'></i></button>}
                {openConfirmationModal && <RemoveDataConfirmationModal closeModal={closeConfirmationModal} removeData={removeSelection}/>}
            </div>
            {fixedDrawer
                ? (drawerNode && overlayRoot && ReactDOM.createPortal(drawerNode, overlayRoot))
                : drawerNode}
        </OutsideClickComponent>

        <BodySimpleModal isDisabled={isDisabled} messages={noticeDisabled}/>
    </div>
}